import { OnChangeFn, SortingState } from '@tanstack/react-table';
import { useCallback, useState } from 'react';
import { TableSortConfig } from '../types';

const EMPTY_SORTING: SortingState = [];

export function useSortingState(sortingConfig?: TableSortConfig) {
  const externalSorting = sortingConfig?.sortingState;
  const onSortingChange = sortingConfig?.onSortingChange;
  // An explicitly supplied state (including undefined for an empty sort) plus
  // a callback is controlled. A callback alone only observes local changes.
  const isControlled = Boolean(
    onSortingChange && sortingConfig && 'sortingState' in sortingConfig
  );
  const [local, setLocal] = useState(() => ({
    preset: externalSorting,
    sorting: externalSorting ?? EMPTY_SORTING,
  }));
  // Preserve editable presets for existing tables without a change handler.
  // Reset only when the caller supplies a different preset, not on local edits.
  const current =
    local.preset === externalSorting
      ? local
      : { preset: externalSorting, sorting: externalSorting ?? EMPTY_SORTING };
  if (current !== local) setLocal(current);

  const sortingState = isControlled
    ? (externalSorting ?? EMPTY_SORTING)
    : current.sorting;
  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      const next =
        typeof updater === 'function' ? updater(sortingState) : updater;
      if (!isControlled) setLocal({ preset: externalSorting, sorting: next });
      onSortingChange?.(next);
    },
    [externalSorting, isControlled, onSortingChange, sortingState]
  );

  return { sortingState, handleSortingChange } as const;
}
